import { getDb } from './index';
import { selectRemoteDirect, syncBatchExecuteRemote } from './tursoSync';

export interface GuestMigrationResult {
  migrated: boolean;
  type: 'auto_uploaded' | 'cloud_loaded' | 'merge_prompt_needed' | 'faculty_mismatch' | 'already_migrated' | 'no_local_data';
  remoteCount?: number;
  localCount?: number;
  localFaculty?: string;
  remoteFaculty?: string;
}

/**
 * Evalúa si el usuario invitado realizó modificaciones o agregados reales en el dispositivo.
 */
export async function hasGuestUserModifications(db: any, localAsignaturas: any[]): Promise<boolean> {
  if (!localAsignaturas || localAsignaturas.length === 0) return false;

  const hasModifiedAsignatura = localAsignaturas.some(a => 
    a.estado !== 'pendiente' || 
    a.nota_final !== null || 
    a.porcentaje_logro !== null || 
    a.calificacion_oficial !== null ||
    (a.aplazos && a.aplazos !== '[]' && (typeof a.aplazos === 'string' ? JSON.parse(a.aplazos) : a.aplazos).length > 0)
  );

  if (hasModifiedAsignatura) return true;

  try {
    const acres = await db.select('SELECT COUNT(*) as cnt FROM acreditaciones_libres');
    if (acres && acres[0]?.cnt > 0) return true;
  } catch (_) {}

  try {
    const evals = await db.select('SELECT COUNT(*) as cnt FROM evaluaciones');
    if (evals && evals[0]?.cnt > 0) return true;
  } catch (_) {}

  try {
    const notas = await db.select('SELECT COUNT(*) as cnt FROM notas_generales WHERE id != $1', ['nota_bienvenida']);
    if (notas && notas[0]?.cnt > 0) return true;
  } catch (_) {}

  return false;
}

/**
 * Recorre todas las tablas locales y las inserta/actualiza en la base de datos remota de Turso en lote (batch).
 */
export async function syncAllLocalDataToRemote(db: any): Promise<void> {
  const tables = [
    { name: 'facultades', keys: ['id', 'nombre', 'sigla', 'disponible', 'escala_calificacion_id', 'terminologia', 'design_tokens_ref'] },
    { name: 'escalas_calificacion', keys: ['id', 'facultad', 'nombre', 'niveles'] },
    { name: 'carreras', keys: ['id', 'facultad_id', 'nombre', 'tipo', 'creditos_totales', 'duracion_semestres', 'locacion', 'malla_precargada', 'modo_vacio_confirmado'] },
    { name: 'planes_estudio', keys: ['id', 'nombre', 'facultad', 'duracion_semestres'] },
    { name: 'modulos', keys: ['id', 'facultad_id', 'nombre', 'creditos_minimos', 'color_hex'] },
    { name: 'ciclos', keys: ['id', 'facultad_id', 'nombre', 'creditos_minimos', 'orden'] },
    { name: 'asignaturas', keys: ['id', 'plan_id', 'nombre', 'codigo', 'semestre_sugerido', 'creditos', 'departamento', 'facultad_origen', 'estado', 'requisitos', 'periodo_cursado', 'fecha_inicio', 'fecha_fin', 'fecha_aprobacion', 'ano_cursado', 'nota_final', 'porcentaje_logro', 'concepto_final', 'posicion_x', 'posicion_y', 'eva_url', 'sala9_url', 'en_papelera', 'es_opcional', 'dictado', 'previas_sugeridas', 'calificacion_oficial', 'aplazos', 'modulo_id', 'ciclo_id'] },
    { name: 'acreditaciones_libres', keys: ['id', 'usuario_id', 'facultad_id', 'modulo_id', 'nombre_actividad', 'creditos', 'fecha_acreditacion'] },
    { name: 'evaluaciones', keys: ['id', 'asignatura_id', 'tipo', 'nombre', 'fecha', 'porcentaje_logro', 'concepto', 'nota', 'escala'] },
    { name: 'materiales', keys: ['id', 'asignatura_id', 'tipo', 'ruta_relativa', 'nombre', 'tags', 'fecha_agregado', 'url_r2'] },
    { name: 'carpetas_notas', keys: ['id', 'nombre', 'color', 'padre_id', 'fecha_creacion'] },
    { name: 'notas_generales', keys: ['id', 'titulo', 'contenido', 'carpeta_id', 'asignatura_id', 'evento_id', 'tags', 'en_papelera', 'fecha_creacion', 'fecha_modificacion'] },
    { name: 'horarios_clases', keys: ['id', 'asignatura_id', 'dia', 'hora_inicio', 'hora_fin', 'grupo', 'salon', 'docente', 'observaciones', 'periodo'] },
    { name: 'eventos_calendario', keys: ['id', 'tipo', 'asignatura_id', 'fecha', 'descripcion', 'hora'] },
    { name: 'historial_academico', keys: ['id', 'asignatura_id', 'fecha', 'instancia', 'resultado', 'calificacion'] },
  ];

  const localAsigIds = new Set<string>();
  const localPlanIds = new Set<string>();
  const localFolderIds = new Set<string>();
  const localModuloIds = new Set<string>();
  const localCicloIds = new Set<string>();

  try {
    const localPlans = await db.select('SELECT id FROM planes_estudio');
    localPlans.forEach((p: any) => localPlanIds.add(p.id));
  } catch (_) {}

  try {
    const localAsigs = await db.select('SELECT id FROM asignaturas');
    localAsigs.forEach((a: any) => localAsigIds.add(a.id));
  } catch (_) {}

  try {
    const localFolders = await db.select('SELECT id FROM carpetas_notas');
    localFolders.forEach((f: any) => localFolderIds.add(f.id));
  } catch (_) {}

  try {
    const localModulos = await db.select('SELECT id FROM modulos');
    localModulos.forEach((m: any) => localModuloIds.add(m.id));
  } catch (_) {}

  try {
    const localCiclos = await db.select('SELECT id FROM ciclos');
    localCiclos.forEach((c: any) => localCicloIds.add(c.id));
  } catch (_) {}

  const batchStatements: Array<{ sql: string; args: any[] }> = [];

  for (const t of tables) {
    try {
      const rows = await db.select(`SELECT * FROM ${t.name}`);
      for (const row of rows) {
        if (t.name === 'asignaturas') {
          if (row.plan_id && !localPlanIds.has(row.plan_id)) {
            const defaultPlanSql = `INSERT OR REPLACE INTO planes_estudio (id, nombre, facultad, duracion_semestres) VALUES ($1, $2, $3, $4)`;
            batchStatements.push({ sql: defaultPlanSql, args: [row.plan_id, 'Plan de Estudios', 'FCEA', 8] });
            localPlanIds.add(row.plan_id);
          }
          if (row.modulo_id && !localModuloIds.has(row.modulo_id)) row.modulo_id = null;
          if (row.ciclo_id && !localCicloIds.has(row.ciclo_id)) row.ciclo_id = null;
        }

        if (t.name === 'acreditaciones_libres') {
          if (row.modulo_id && !localModuloIds.has(row.modulo_id)) row.modulo_id = null;
        }

        if (['evaluaciones', 'materiales', 'horarios_clases', 'eventos_calendario', 'historial_academico'].includes(t.name)) {
          if (row.asignatura_id && !localAsigIds.has(row.asignatura_id)) continue;
        }

        if (t.name === 'notas_generales') {
          if (row.asignatura_id && !localAsigIds.has(row.asignatura_id)) row.asignatura_id = null;
          if (row.carpeta_id && !localFolderIds.has(row.carpeta_id)) row.carpeta_id = null;
        }

        const columns = t.keys.filter(k => row[k] !== undefined);
        const placeholders = columns.map((_, i) => `$${i + 1}`).join(', ');
        const values = columns.map(k => {
          const val = row[k];
          if (typeof val === 'boolean') return val ? 1 : 0;
          if (typeof val === 'object' && val !== null) return JSON.stringify(val);
          return val === undefined ? null : val;
        });

        const sql = `INSERT OR REPLACE INTO ${t.name} (${columns.join(', ')}) VALUES (${placeholders})`;
        batchStatements.push({ sql, args: values });
      }
    } catch (err) {
      console.warn(`[GuestMigration] Error armando batch para tabla ${t.name}:`, err);
    }
  }

  if (batchStatements.length > 0) {
    const allStatements = [
      { sql: 'PRAGMA foreign_keys = OFF;', args: [] },
      ...batchStatements,
      { sql: 'PRAGMA foreign_keys = ON;', args: [] }
    ];

    await syncBatchExecuteRemote(allStatements);
  }
}

/**
 * Descarga todos los datos remotos de Turso a la base SQLite local.
 */
export async function syncAllRemoteDataToLocal(db: any, remoteAsignaturas: any[] = []): Promise<void> {
  const tablesForward = ['facultades', 'escalas_calificacion', 'carreras', 'planes_estudio', 'modulos', 'ciclos', 'asignaturas', 'acreditaciones_libres', 'evaluaciones', 'materiales', 'carpetas_notas', 'notas_generales', 'horarios_clases', 'eventos_calendario', 'historial_academico'];
  const tablesReverse = [...tablesForward].reverse();

  // 1. Descargar todos los registros remotos a memoria primero
  const remoteDataMap = new Map<string, any[]>();
  for (const table of tablesForward) {
    try {
      if (table === 'asignaturas' && remoteAsignaturas.length > 0) {
        remoteDataMap.set(table, remoteAsignaturas);
      } else {
        const rows = await selectRemoteDirect<any[]>(`SELECT * FROM ${table}`);
        remoteDataMap.set(table, rows || []);
      }
    } catch (err) {
      console.warn(`[GuestMigration] Error leyendo tabla remota ${table}:`, err);
      remoteDataMap.set(table, []);
    }
  }

  // 2. Desactivar Foreign Keys durante el reemplazo en lote local
  try {
    await db.execute('PRAGMA foreign_keys = OFF;');
  } catch (_) {}

  // 3. Borrar las tablas locales en orden inverso (hijas primero)
  for (const table of tablesReverse) {
    const rows = remoteDataMap.get(table);
    if (rows && rows.length > 0) {
      try {
        await db.execute(`DELETE FROM ${table}`);
      } catch (err) {
        console.warn(`[GuestMigration] Error limpiando tabla local ${table}:`, err);
      }
    }
  }

  // 4. Insertar datos remotos en orden directo (padres primero)
  for (const table of tablesForward) {
    const rows = remoteDataMap.get(table);
    if (rows && rows.length > 0) {
      for (const row of rows) {
        try {
          const keys = Object.keys(row);
          const placeholders = keys.map((_, i) => `$${i + 1}`).join(', ');
          const values = Object.values(row).map(val => (typeof val === 'object' && val !== null ? JSON.stringify(val) : val));
          const sql = `INSERT OR REPLACE INTO ${table} (${keys.join(', ')}) VALUES (${placeholders})`;
          await db.execute(sql, values);
        } catch (err) {
          console.warn(`[GuestMigration] Error insertando fila remota en ${table}:`, err);
        }
      }
    }
  }

  // 5. Reactivar Foreign Keys
  try {
    await db.execute('PRAGMA foreign_keys = ON;');
  } catch (_) {}
}

/**
 * Detecta la facultad predominante en un listado de asignaturas.
 */
export function detectFacultyFromAsignaturas(rows: any[]): string | null {
  if (!rows || rows.length === 0) return null;
  const fceaCount = rows.filter(a => 
    a.facultad_origen === 'fcea' || 
    (a.plan_id && (a.plan_id.includes('fcea') || a.plan_id.includes('tea')))
  ).length;
  const psicoCount = rows.filter(a => 
    a.facultad_origen === 'psico' || 
    (a.plan_id && a.plan_id.includes('psico'))
  ).length;

  if (psicoCount > fceaCount) return 'psico';
  if (fceaCount > 0) return 'fcea';
  return null;
}

/**
 * Evalúa y ejecuta el flujo de migración al loguearse/registrarse desde modo invitado.
 */
export async function checkAndMigrateGuestData(userId: string): Promise<GuestMigrationResult> {
  const migrationKey = `malla_guest_migrated_${userId}`;
  if (localStorage.getItem(migrationKey) === 'true') {
    return { migrated: false, type: 'already_migrated' };
  }

  const db = await getDb();

  // 1. Obtener conteo de asignaturas locales de invitado
  const localAsignaturas = await db.select<any[]>('SELECT * FROM asignaturas');
  const hasLocalGuestData = localAsignaturas.length > 0;

  // 2. Consultar asignaturas en la nube (Turso)
  const remoteAsignaturas = await selectRemoteDirect<any[]>('SELECT * FROM asignaturas');
  const hasRemoteCloudData = remoteAsignaturas.length > 0;

  // 3. Determinar facultades local y remota
  const storedLocalFac = localStorage.getItem('malla_active_facultad_id') || 'fcea';
  const localFaculty = detectFacultyFromAsignaturas(localAsignaturas) || storedLocalFac;
  const remoteFaculty = detectFacultyFromAsignaturas(remoteAsignaturas) || localFaculty;

  if (!hasLocalGuestData && !hasRemoteCloudData) {
    localStorage.setItem(migrationKey, 'true');
    return { migrated: false, type: 'no_local_data', localFaculty, remoteFaculty };
  }

  // CASO A: Hay datos tanto locales de invitado como en la nube
  if (hasLocalGuestData && hasRemoteCloudData) {
    const hasEdits = await hasGuestUserModifications(db, localAsignaturas);

    if (!hasEdits) {
      console.info('[GuestMigration] Usuario invitado sin ediciones locales reales. Cargando datos de la nube automáticamente...');
      await syncAllRemoteDataToLocal(db, remoteAsignaturas);
      localStorage.setItem('malla_active_facultad_id', remoteFaculty);
      localStorage.setItem(migrationKey, 'true');
      return { 
        migrated: true, 
        type: 'cloud_loaded', 
        remoteCount: remoteAsignaturas.length,
        localFaculty: remoteFaculty,
        remoteFaculty
      };
    }

    // Si la facultad del invitado difiere de la facultad de la cuenta de la nube (ej. Invitado en Psico, Cuenta en FCEA)
    if (localFaculty !== remoteFaculty) {
      console.info(`[GuestMigration] Discrepancia de facultad detectada: Local=${localFaculty}, Remoto=${remoteFaculty}`);
      return {
        migrated: false,
        type: 'faculty_mismatch',
        localFaculty,
        remoteFaculty,
        localCount: localAsignaturas.length,
        remoteCount: remoteAsignaturas.length
      };
    }

    // Mismas facultades y hay ediciones locales reales: solicitar fusión o descarte
    console.info('[GuestMigration] Se detectaron ediciones locales reales del invitado y datos en la nube. Solicitando decisión...');
    return {
      migrated: false,
      type: 'merge_prompt_needed',
      localFaculty,
      remoteFaculty,
      remoteCount: remoteAsignaturas.length,
      localCount: localAsignaturas.length
    };
  }

  // CASO B: Cuenta vacía en la nube Y hay datos locales de invitado
  if (!hasRemoteCloudData && hasLocalGuestData) {
    console.info('[GuestMigration] Caso 1: Subiendo automáticamente datos de invitado a la nube...');
    await syncAllLocalDataToRemote(db);
    localStorage.setItem(migrationKey, 'true');
    return { 
      migrated: true, 
      type: 'auto_uploaded', 
      localCount: localAsignaturas.length,
      localFaculty,
      remoteFaculty: localFaculty
    };
  }

  // CASO C: Cuenta con datos en la nube Y sin datos locales de invitado
  if (hasRemoteCloudData && !hasLocalGuestData) {
    console.info(`[GuestMigration] Caso 3: Cargando datos de la cuenta remota (${remoteFaculty})...`);
    await syncAllRemoteDataToLocal(db, remoteAsignaturas);
    
    // Auto-switch a la facultad remota de la cuenta
    localStorage.setItem('malla_active_facultad_id', remoteFaculty);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-faculty', remoteFaculty);
    }

    localStorage.setItem(migrationKey, 'true');
    return { 
      migrated: true, 
      type: 'cloud_loaded', 
      remoteCount: remoteAsignaturas.length,
      localFaculty: remoteFaculty,
      remoteFaculty
    };
  }

  return { migrated: false, type: 'already_migrated' };
}

/**
 * Resuelve la decisión tomada en el caso 2 (Fusionar o Descartar) o choque de facultades.
 */
export async function resolveGuestMigrationDecision(
  userId: string, 
  decision: 'merge' | 'discard', 
  remoteFaculty?: string,
  db?: any
): Promise<void> {
  const database = db || await getDb();
  const migrationKey = `malla_guest_migrated_${userId}`;

  if (decision === 'merge') {
    // Subir los datos locales faltantes a la nube y luego traer todo
    await syncAllLocalDataToRemote(database);
    const updatedRemote = await selectRemoteDirect<any[]>('SELECT * FROM asignaturas');
    await syncAllRemoteDataToLocal(database, updatedRemote);
  } else {
    // Descartar locales: Sobrescribir todo localmente con lo que está en la nube
    const remoteAsignaturas = await selectRemoteDirect<any[]>('SELECT * FROM asignaturas');
    await syncAllRemoteDataToLocal(database, remoteAsignaturas);
  }

  if (remoteFaculty) {
    localStorage.setItem('malla_active_facultad_id', remoteFaculty);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-faculty', remoteFaculty);
    }
  }

  localStorage.setItem(migrationKey, 'true');
}
